import React, { useState, useEffect } from "react";
import styles from "./EventLogs.module.css";
import { useGetPostsQuery } from "../../../api/apiConfig";

interface Event {
  Time: string;
  EventType: string;
  DoneBy: string | null;
  Source: string;
}

interface EventLogModel {
  RecordCount?: number | null;
  LstRecords?: Event[];
}

interface EventLogData {
  StatusCode: number;
  Status: string;
  TotalRecords: number | null;
  Message: string;
  Model?: EventLogModel;
  Value: any | null;
}

const EventLogs: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState("7days");
  const [queryParams, setQueryParams] = useState({ fromDate: "", toDate: "" });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const calculateDateRange = (range: string) => {
    let now = new Date();
    now.setDate(now.getDate() - 1);

    let fromDate = new Date();
    fromDate.setUTCHours(0, 0, 0, 0);
    switch (range) {
      case "7days":
        fromDate.setDate(now.getDate() - 7);
        break;
      case "1month":
        fromDate.setMonth(now.getMonth() - 1);
        break;
      case "6months":
        fromDate.setMonth(now.getMonth() - 6);
        break;
      case "1year":
        fromDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        fromDate.setDate(now.getDate() - 7);
    }
    return { fromDate: fromDate.toISOString(), toDate: now.toISOString() };
  };

  useEffect(() => {
    const { fromDate, toDate } = calculateDateRange(dateRange);
    setQueryParams({ fromDate, toDate });
  }, [dateRange]);

  let {
    isLoading: isEventLogLoading,
    isSuccess: isEventLogSuccess,
    data: eventLogData,
    refetch: fetchEventLogs
  } = useGetPostsQuery(
    `PrintingRequest/GetEventLog?fromDate=${queryParams?.fromDate}&toDate=${queryParams?.toDate}&PageNumber=${page}&PageSize=${rowsPerPage}`
  );

  let newData = eventLogData as EventLogData;

  const handleNextPage = () => {
    if (
      newData &&
      newData.Model &&
      page < Math.ceil(newData?.Model?.RecordCount || 0 / rowsPerPage)
    ) {
      setPage(page + 1);
    }
  };

  const handlePreviousPage = () => {
    if (newData && page > 1) {
      setPage(page - 1);
    }
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  useEffect(() => {
    if (isEventLogSuccess && newData && newData?.Model?.LstRecords) {
      const eventsData: Event[] =
        newData?.Model &&
        newData?.Model?.LstRecords?.map((event: any) => ({
          Time: event.Time,
          EventType: event.EventType,
          DoneBy: event.DoneBy,
          Source: event.Source
        }));
      setEvents(eventsData);
      filterEvents(eventsData);
    }
  }, [eventLogData, isEventLogSuccess, newData]);

  useEffect(() => {
    filterEvents();
  }, [dateRange, searchTerm]);

  const filterEvents = (initialEvents = events) => {
    let filtered = initialEvents;

    let startDate = new Date(queryParams?.fromDate);
    if (!queryParams?.fromDate || isNaN(startDate.getTime())) {
      startDate = new Date();
    }
    filtered = filtered.filter((event: any) => {
      const eventDate = new Date(event.Time);
      switch (dateRange) {
        case "7days":
          return startDate.getTime() - eventDate.getTime() <= 7 * 24 * 60 * 60 * 1000;
        case "1month":
          return (
            startDate.getTime() - eventDate.getTime() <= 30 * 24 * 60 * 60 * 1000
          );
        case "6months":
          return (
            startDate.getTime() - eventDate.getTime() <= 6 * 30 * 24 * 60 * 60 * 1000
          );
        case "1year":
          return (
            startDate.getTime() - eventDate.getTime() <= 365 * 24 * 60 * 60 * 1000
          );
        default:
          return true;
      }
    });

    if (searchTerm) {
      filtered = filtered.filter((event: any) =>
        Object.values(event).some((value) =>
          value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    setFilteredEvents(filtered);
  };

  const handleSearch = () => {
    filterEvents();
  };

  const handleDateRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDateRange(e.target.value);
    fetchEventLogs();
  };

  return (
    <div className={styles.container}>
      <h4>Event Log</h4>
      <p>View the logged events in the Smartprint system.</p>
      <div className={styles.row}>
        <select
          className={styles.dropdown}
          value={dateRange}
          onChange={handleDateRangeChange}
        >
          <option value="7days">Date Range: 7 days</option>
          <option value="1month">Date Range: 1 month</option>
          <option value="6months">Date Range: 6 months</option>
          <option value="1year">Date Range: 1 year</option>
        </select>
      </div>
      <div className={styles.tableContainer}>
        <div className={styles.searchBox}>
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className={styles.searchIcon} onClick={handleSearch}>
            {/* 🔍 */}
          </span>
        </div>
        <div className={styles.tableBox}>
          <table
            className={`table table-striped table-borderless ${styles.table}`}
          >
            <thead>
              <tr>
                <th>Source</th>
                <th>Done By</th>
                <th>Event Type</th>
                <th>Time</th>
              </tr>
            </thead>
            {isEventLogLoading === true ? (
              <tbody>
                <tr>
                  <td colSpan={5}>
                    <div>
                      <div className={styles.loadingContainer}>
                        <div className={styles.loadingSpinner}></div>
                        <div className={styles.loadingText}>Loading...</div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {filteredEvents &&
                  filteredEvents?.map((event, index) => ( 
                    <tr key={index}>
                      <td>{event.Source}</td>
                      <td>{event.DoneBy || "N/A"}</td>
                      <td>{event.EventType}</td>
                      <td>
                        {new Date(event.Time).toLocaleString("en-UK", {
                          timeZone: "Europe/London",
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: true
                        })}
                      </td>{" "}
                    </tr>
                  ))}
              </tbody>
            )}
          </table>
          <div className={styles.pagination}>
            <button onClick={handlePreviousPage} disabled={page === 1}>
              Previous
            </button>
            <span>
              Page {page} of{" "}
              {newData?.Model?.RecordCount
                ? Math.ceil(newData?.Model?.RecordCount / rowsPerPage)
                : 0}
            </span>
            <button
              onClick={handleNextPage}
              disabled={
                !newData?.Model?.RecordCount ||
                page === Math.ceil(newData?.Model?.RecordCount / rowsPerPage)
              }
            >
              Next
            </button>
          </div>
          <div className={styles.rowsPerPage}>
            <label htmlFor="rowsPerPage">Rows per page:</label>
            <select
              id="rowsPerPage"
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={40}>40</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventLogs;
